// @flow

import React from 'react';
import styled from 'styled-components';
import { createFragmentContainer, graphql } from 'react-relay';
import { i18n } from 'shared/utils';
import BOMCostBreakdown from 'main-app/components/BOMCostBreakdown';
import CostVarianceAlert from 'main-app/components/CostVarianceAlert';
import BOMCostTrendChart from 'main-app/components/BOMCostTrendChart';
import type { ItemCostAnalysis_item as ItemFragment } from './__generated__/ItemCostAnalysis_item';

type Props = {
  item: ItemFragment,
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const ItemCostAnalysis = ({ item }: Props) => {
  // Use real data from backend
  const targetCost = item.cost || 0; // Manual cost is the "target"
  const actualCost = item.totalCalculatedCost || 0; // Calculated cost is "actual"
  const variance = targetCost > 0 ? ((actualCost - targetCost) / targetCost) * 100 : 0;

  const machineCost = item.calculatedMachineCost || 0;
  const materialCost = item.calculatedMaterialCost || 0;

  return (
    <Wrapper>
      {/* Cost Variance Alert */}
      <CostVarianceAlert
        variance={variance}
        targetCost={targetCost}
        actualCost={actualCost}
      />

      {/* Cost Breakdown Panel */}
      <BOMCostBreakdown
        itemName={item.name}
        machineCost={machineCost}
        materialCost={materialCost}
        totalCost={actualCost}
        childItems={item.childItems}
      />

      {/* Historical Trend Chart */}
      <BOMCostTrendChart 
        costHistory={item.costHistory || []}
        currentCost={actualCost}
        targetCost={targetCost}
      />
    </Wrapper>
  );
};

export default createFragmentContainer(ItemCostAnalysis, {
  item: graphql`
    fragment ItemCostAnalysis_item on Item {
      id
      name
      cost
      calculatedMachineCost
      calculatedMaterialCost
      totalCalculatedCost
      costHistory(daysBack: 90) {
        calculatedMachineCost
        calculatedMaterialCost
        totalCalculatedCost
        targetCost
        snapshotDate
      }
      childItems(first: null)
        @connection(key: "ItemCostAnalysis_childItems", filters: []) {
        edges {
          node {
            id
            itemNumber
            name
            cost
            totalCalculatedCost
          }
          usageQty
        }
      }
    }
  `,
});
